import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { CropRounded, FlipRounded, RestartAltRounded, RotateLeftRounded, RotateRightRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogContent,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from '../MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';

/** Longest edge kept; the server caps at the same size. */
const MAX_EDGE = 1600;

type Aspect = 'free' | 'square' | 'photo';
const ASPECT_RATIO: Record<Aspect, number> = { free: NaN, square: 1, photo: 4 / 3 };

interface Props {
	/** Image to edit, already loaded locally (object URL or data URL). */
	src: string | null;
	busy?: boolean;
	onCancel: () => void;
	onDone: (blob: Blob) => void;
}

/** Every chosen line image passes through here: crop, rotate, flip. */
const ImageEditor = ({ src, busy = false, onCancel, onDone }: Props) => {
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();
	const [cropper, setCropper] = useState<any>(null);
	const [aspect, setAspect] = useState<Aspect>('free');
	const [flipped, setFlipped] = useState(false);

	useEffect(() => {
		setAspect('free');
		setFlipped(false);
	}, [src]);

	useEffect(() => {
		cropper?.setAspectRatio(ASPECT_RATIO[aspect]);
	}, [cropper, aspect]);

	const handleDone = () => {
		if (!cropper) return;
		const canvas: HTMLCanvasElement = cropper.getCroppedCanvas({
			maxWidth: MAX_EDGE,
			maxHeight: MAX_EDGE,
			fillColor: '#ffffff',
			imageSmoothingQuality: 'high',
		});
		canvas.toBlob((blob) => { if (blob) onDone(blob); }, 'image/jpeg', 0.9);
	};

	return (
		<MoleDialog
			open={Boolean(src)}
			onClose={busy ? undefined : onCancel}
			maxWidth="md"
			fullWidth
			themeColor={themeColor}
			// Opens after the picker at the same level, so it lands on top of it.
			sx={{ zIndex: Z_INDEX.deepDialog }}
		>
			<MoleDialogHeader
				title={t('common:docImages.editorTitle')}
				icon={<CropRounded />}
				onClose={busy ? undefined : onCancel}
				themeColor={themeColor}
			/>
			<MoleDialogContent>
				<Box sx={{ height: { xs: 320, sm: 440 }, bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden' }}>
					{src && (
						<Cropper
							src={src}
							style={{ height: '100%', width: '100%' }}
							viewMode={1}
							autoCropArea={1}
							background={false}
							responsive
							dragMode="move"
							checkOrientation
							onInitialized={(instance: any) => setCropper(instance)}
						/>
					)}
				</Box>
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
					<Tooltip title={t('common:docImages.rotateLeft')}>
						<IconButton onClick={() => cropper?.rotate(-90)} disabled={busy}><RotateLeftRounded /></IconButton>
					</Tooltip>
					<Tooltip title={t('common:docImages.rotateRight')}>
						<IconButton onClick={() => cropper?.rotate(90)} disabled={busy}><RotateRightRounded /></IconButton>
					</Tooltip>
					<Tooltip title={t('common:docImages.flip')}>
						<IconButton
							onClick={() => { cropper?.scaleX(flipped ? 1 : -1); setFlipped(!flipped); }}
							disabled={busy}
							sx={flipped ? { color: themeColor } : undefined}
						>
							<FlipRounded />
						</IconButton>
					</Tooltip>
					<Tooltip title={t('common:docImages.reset')}>
						<IconButton onClick={() => { cropper?.reset(); setFlipped(false); setAspect('free'); }} disabled={busy}>
							<RestartAltRounded />
						</IconButton>
					</Tooltip>
					<ToggleButtonGroup
						size="small"
						exclusive
						value={aspect}
						onChange={(_, value: Aspect | null) => value && setAspect(value)}
						disabled={busy}
					>
						{(['free', 'square', 'photo'] as Aspect[]).map((value) => (
							<ToggleButton key={value} value={value} sx={{ px: 1.5, fontWeight: 700 }}>
								{t(`common:docImages.aspect.${value}`)}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				</Stack>
			</MoleDialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={onCancel} disabled={busy}>{t('common:ui.buttons.cancel')}</MoleCancelButton>
				<MolePrimaryButton
					onClick={handleDone}
					disabled={!cropper || busy}
					startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
				>
					{t('common:docImages.useImage')}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default ImageEditor;
